import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Download, Search, Play, Pause, Lock, Filter, Loader2, Edit2 } from 'lucide-react';
import { crmMailingListsApi, CrmMailingList } from '../services/crmApi';
import { toast } from 'sonner';
export function MailingListDetail() {
  const {
    listId
  } = useParams<{
    listId: string;
  }>();
  const [list, setList] = useState<CrmMailingList | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'subscribers' | 'submissions'>('subscribers');
  useEffect(() => {
    const fetchList = async () => {
      if (!listId) return;
      setLoading(true);
      try {
        const data = await crmMailingListsApi.get(listId);
        setList(data);
      } catch (err) {
        console.error('Failed to fetch list:', err);
        toast.error('Failed to load mailing list details');
      } finally {
        setLoading(false);
      }
    };
    fetchList();
  }, [listId]);
  const getIntakeStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return {
          icon: Play,
          color: 'text-green-600',
          bg: 'bg-green-50',
          label: 'Accepting Signups'
        };
      case 'paused':
        return {
          icon: Pause,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          label: 'Paused'
        };
      case 'closed':
        return {
          icon: Lock,
          color: 'text-red-600',
          bg: 'bg-red-50',
          label: 'Closed'
        };
      default:
        return {
          icon: Play,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          label: 'Unknown'
        };
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4982" data-mp="3gp0ap1ffcdsm">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4983" data-mp="3lnbmhhpa4ydh" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4984" data-mp="3llqthwim5nhq">
          Loading list details...
        </span>
      </div>;
  }
  if (!list) {
    return <div className="text-center py-12" data-id="element-4985" data-mp="1hk08kzxhfvm8">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-4986" data-mp="1hixhaqw8zr5s">
          Mailing list not found
        </h2>
        <Link to="/mailing-lists" className="text-blue-600 hover:text-blue-800 mt-2 inline-block" data-id="element-4987" data-mp="1vigqln0sp8jo">
          ← Back to Mailing Lists
        </Link>
      </div>;
  }
  const intakeConfig = getIntakeStatusConfig(list.Status);
  const IntakeIcon = intakeConfig.icon;
  return <div className="space-y-6" data-id="element-4988" data-mp="xxay8kx8u8qm">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-4989" data-mp="1cknmwiickul8">
        <div className="flex items-start gap-4" data-id="element-4990" data-mp="1cm754enkdfpn">
          <Link to="/mailing-lists" className="p-2 hover:bg-gray-100 rounded-full text-gray-500 mt-1" data-id="element-4991" data-mp="17smex8s7pq10">
            <ArrowLeft className="h-5 w-5" data-id="element-4992" data-mp="k2081kty2bsl" />
          </Link>
          <div data-id="element-4993" data-mp="f3yqj4c62kj8">
            <div className="flex items-center gap-3" data-id="element-4994" data-mp="f4gtejtc3x04">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4995" data-mp="2qslljvn5z67j">
                {list.Name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${intakeConfig.bg} ${intakeConfig.color}`} data-id="element-4996" data-mp="2vjbrpb3ezd3a">
                <IntakeIcon className="h-3 w-3" data-id="element-4997" data-mp="2lsz9hpj2nw0b" />
                {intakeConfig.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-4998" data-mp="39jwc2boz9gyw">
              {list.Description || 'No description'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-4999" data-mp="3efk0upzjs83v">
          <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-5000" data-mp="30h3mqdzcvfbp">
            <Edit2 className="h-4 w-4" data-id="element-5001" data-mp="1p39h6qy5zqa8" />
            Edit
          </button>
          <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-5002" data-mp="1p5ck2bkf1dwd">
            <Download className="h-4 w-4" data-id="element-5003" data-mp="29dape6sx02zj" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4" data-id="element-5004" data-mp="24j6hnlbtrqxe">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5005" data-mp="1zmy8cokb6ry5">
          <div className="flex items-center gap-3" data-id="element-5006" data-mp="1vkl3019h4w1w">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-5007" data-mp="1vm5x5prvmcz8">
              <Users className="h-5 w-5 text-blue-600" data-id="element-5008" data-mp="2sb3pl1c5lyt8" />
            </div>
            <div data-id="element-5009" data-mp="2nbheaxfvysn2">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5010" data-mp="2nazc7k6jlk78">
                {(list.Subscriber_Count || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500" data-id="element-5011" data-mp="2j82o5dy8gvvw">Total Subscribers</p>
            </div>
          </div>
        </div>
        {/* Placeholder stats for now since we don't have subscriber details from CRM yet */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 opacity-50" data-id="element-5012" data-mp="17snrkb9xs61b">
          <div className="flex items-center gap-3" data-id="element-5013" data-mp="12ucg5w3ztngz">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-5014" data-mp="3ejixegbobjp4">
              <Mail className="h-5 w-5 text-green-600" data-id="element-5015" data-mp="3egwifvmge1wv" />
            </div>
            <div data-id="element-5016" data-mp="3jiltvcdl8hlh">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5017" data-mp="3jfzn3eyiu64b">-</p>
              <p className="text-xs text-gray-500" data-id="element-5018" data-mp="3nivzgnxgx5zt">Confirmed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-5019" data-mp="3sc8nowy8jas1">
        <nav className="-mb-px flex gap-6" data-id="element-5020" data-mp="131iuvup4r4c">
          <button onClick={() => setActiveTab('subscribers')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'subscribers' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5021" data-mp="16gyq0w7246t">
            Subscribers
          </button>
          <button onClick={() => setActiveTab('submissions')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5022" data-mp="2cuf7h7yg3h54">
            Form Submissions
          </button>
        </nav>
      </div>

      {/* Content Placeholder */}
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-5023" data-mp="3t72ycpb1k7iw">
        <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-5024" data-mp="3p4pwibzf0tre" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5025" data-mp="3p7c42bmb399m">
          Subscriber data syncing...
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-5026" data-mp="20jgiq6sfhb3">
          Detailed subscriber lists are being synced from Zoho CRM.
        </p>
      </div>
    </div>;
}